import { useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/shared/utils';
import { Tooltip } from '@/shared/components/ui/tooltip';
import { useSidebar } from './SidebarContext';
import { useUserPreferences } from '@/features/dashboard/hooks/useDashboard';
import {
  LayoutDashboard,
  FolderKanban,
  Server,
  History,
  Package,
  Boxes,
  Cable,
  RefreshCw,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react';

interface NavItem {
  name: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

function buildNavGroups(dashboardEnabled: boolean): NavGroup[] {
  return [
    ...(dashboardEnabled
      ? [
          {
            items: [
              { name: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
            ],
          },
        ]
      : []),
    {
      label: 'Monitor',
      items: [
        { name: 'Projects', to: '/projects', icon: FolderKanban },
        { name: 'Environments', to: '/environments', icon: Server },
        { name: 'Container Apps', to: '/apps', icon: Boxes },
        { name: 'Recent Changes', to: '/history', icon: History },
      ],
    },
    {
      label: 'Reference',
      items: [
        { name: 'Image Repos', to: '/images', icon: Package },
      ],
    },
    {
      label: 'Settings',
      items: [
        { name: 'Connections', to: '/connections', icon: Cable },
        { name: 'Sync Status', to: '/sync', icon: RefreshCw },
      ],
    },
  ];
}

function SidebarNavItem({
  item,
  collapsed,
  onNavigate,
}: Readonly<{
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}>) {
  const link = (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center rounded-lg text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )
      }
    >
      <item.icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{item.name}</span>}
    </NavLink>
  );

  if (collapsed) {
    return <Tooltip content={item.name} side="right">{link}</Tooltip>;
  }

  return link;
}

function SidebarContent({
  collapsed,
  navGroups,
  onNavigate,
}: Readonly<{
  collapsed: boolean;
  navGroups: NavGroup[];
  onNavigate?: () => void;
}>) {
  return (
    <nav className="flex-1 space-y-4 px-2 py-4 overflow-y-auto">
      {navGroups.map((group, groupIdx) => (
        <div key={group.label ?? `group-${groupIdx}`}>
          {group.label && !collapsed && (
            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>
          )}
          {group.label && collapsed && (
            <div className="my-2 border-t" />
          )}
          <div className="space-y-1">
            {group.items.map((item) => (
              <SidebarNavItem
                key={item.name}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export default function Sidebar() {
  const { collapsed, toggle, mobileOpen, setMobileOpen } = useSidebar();
  const location = useLocation();
  const { preferences } = useUserPreferences();

  const dashboardEnabled = preferences?.dashboard_preferences?.enabled ?? false;
  const navGroups = useMemo(() => buildNavGroups(dashboardEnabled), [dashboardEnabled]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden md:flex md:flex-col border-r bg-muted/40 transition-all duration-200',
          collapsed ? 'md:w-14' : 'md:w-64'
        )}
      >
        <SidebarContent collapsed={collapsed} navGroups={navGroups} />

        <div className="border-t px-2 py-2">
          <button
            onClick={toggle}
            className="flex w-full items-center justify-center rounded-lg px-2 py-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <div className="flex w-full items-center gap-3 px-1">
                <ChevronsLeft className="h-4 w-4" />
                <span className="text-sm font-medium">Collapse</span>
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <aside className="absolute inset-y-0 left-0 w-64 border-r bg-background shadow-lg flex flex-col animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="text-sm font-semibold">Navigation</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                aria-label="Close navigation"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarContent collapsed={false} navGroups={navGroups} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}
    </>
  );
}
